import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import Img from 'gatsby-image/withIEPolyfill';

import { T2B } from './Text';
import { linkRenderer } from '../helpers/renderers';

import { media } from '../styles/constants';
import { small } from '../styles/text';

const LogoContainer = styled.div`
  display: flex;
  flex-direction: ${props =>
    typeof props.text !== 'undefined' ? 'row' : 'column'};

  ${props =>
    props.center &&
    css`
      width: 13.2rem;
      align-items: center;

      @media (min-width: ${media.phone}) {
        width: 15rem;
      }
    `}
`;

const SVGContainer = styled.div`
  max-width: 13rem;
  height: 6rem;

  @media (min-width: ${media.tabletPortrait}) {
    max-width: 13rem;
  }

  @media (min-width: ${media.desktop}) {
    height: 7rem;
    max-width: 14rem;
  }

  ${props =>
    props.center
      ? css`
          img {
            width: auto;
            height: 100%;
            max-width: 100%;

            @media all and (-ms-high-contrast: none),
              (-ms-high-contrast: active) {
              /* IE cannot have 100% height */
              height: auto;
              max-height: 100%;
            }
          }
        `
      : css`
          position: relative;

          img {
            position: absolute;
            bottom: 0;
            left: 0;
            max-height: 100%;
            max-width: 100%;
          }
        `}
`;

const ImageContainer = styled.div`
  max-width: 15rem;
  width: 100%;
  height: 6rem;
  position: relative;

  @media (min-width: ${media.desktop}) {
    height: 7rem;
  }

  ${props =>
    props.center
      ? css`
          img {
            position: absolute;
            width: auto;
            max-width: 100%;
            max-height: 100%;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
        `
      : css`
          img {
            position: absolute;
            bottom: 0;
            left: 0;
            max-width: 100%;
            height: auto;
          }
        `}
`;

/* [Style] Tweak here to change the attached logo text */
const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 2rem;
  padding-bottom: 4rem;
  max-width: 100%;

  @media (min-width: ${media.desktop}) {
    padding-bottom: 2rem;
  }

  ${props =>
    props.center
      ? css`
          text-align: center;

          /* Needed to add this abstraction because React markdown renders a child p */
          /* T1R */
          .md > * {
            ${small}
          }
        `
      : css`
          /* T2R */
          .md > * {
            font-weight: 400;
          }
        `}
`;

const Logo = ({ imgSrc, imgAlt, title, text, center }) => {
  return (
    <LogoContainer center={center}>
      {typeof imgSrc === 'string' && imgSrc.includes('.svg') && (
        <SVGContainer center={center}>
          <img src={imgSrc} alt={imgAlt} />
        </SVGContainer>
      )}
      {typeof imgSrc === 'string' && !imgSrc.includes('.svg') && (
        <ImageContainer center={center}>
          <img src={imgSrc} alt={imgAlt} />
        </ImageContainer>
      )}
      {typeof imgSrc === 'object' && (
        <ImageContainer center={center}>
          <Img fixed={imgSrc.fixed} alt={imgAlt} />
        </ImageContainer>
      )}
      <TextContainer center={center}>
        {title && <T2B>{title}</T2B>}
        {text && (
          <ReactMarkdown
            className="md"
            source={text}
            renderers={{ link: linkRenderer }}
          />
        )}
      </TextContainer>
    </LogoContainer>
  );
};

Logo.propTypes = {
  imgSrc: PropTypes.string.isRequired,
  imgAlt: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.string,
  center: PropTypes.bool
};

Logo.defaultProps = {
  center: false
};

export default Logo;
